// Smoothly move the page to the section with this HTML id.
// `offset` is the space to leave above the section for the fixed app bar;
// it defaults to 80 pixels.
export default function scrollToSection(sectionId, offset = 80) {
  // A menu item may refer to a section that is not on the current page.
  // In that case there is nothing to scroll to, so leave the page as it is.
  const section = document.getElementById(sectionId);
  if (!section) return;

  // getBoundingClientRect().top measures from the top of the visible window.
  // Add how far the page has already scrolled to get the section's position
  // from the top of the full document, then subtract the app-bar clearance.
  const top = section.getBoundingClientRect().top + window.scrollY - offset;

  // Scroll positions cannot go above the start of the page, so clamp negative
  // results to zero. Let the browser animate the movement; if the user chooses
  // another section before it finishes, the new scroll request takes over.
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

/*

Understanding the Full Document
• The Viewport: The visible window or screen area that displays only the portion of the web page fitting your current monitor or phone display.
• Above-the-Fold: The top section of the web page that appears on your screen immediately without requiring you to scroll down.
• Below-the-Fold: The remaining lower sections of the web page—such as deeper articles, product lists, and the footer—that require you to scroll down to view.
• The HTML Document: The underlying code structure (enclosed in <html> tags) contains every single element—both visible and hidden metadata—for the entire page as a single continuous 
  

Scrolling theory

  The document is the whole web page, including the parts above and below the
  screen(viewport). The viewport is the part currently visible in the browser window.
  When you scroll down, the document stays in place and the viewport shows a
  different part of it. `window.scrollY` tells us how far down we have moved.

  `getBoundingClientRect().top` measures an element's distance from the top of
  the viewport. To find its position in the whole document, add `window.scrollY`.
  This gives the document position used in the `top` calculation above.

  For example, if the page is scrolled down 500 pixels and a section is 700 pixels
  below the viewport's top, the section is 1,200 pixels from the document's top.
  With an 80-pixel header clearance, scrolling to 1,120 pixels places the section
  80 pixels below the viewport's top, where the fixed app bar will not cover it.

  `window.scrollTo` asks the browser to move the viewport to that document
  position. `behavior: "smooth"` lets the browser animate the movement instead
  of jumping there instantly. The `Math.max` call prevents asking the browser to
  scroll to a negative position, which would be above the start of the document.

  What happens when this function is called

  Imagine that you tap the "Skills" item in the menu. The menu calls this
  function with "skills", and the function looks through the page for the
  section whose id is "skills". If that section is not on this page, the story
  ends there: the function returns without moving anything.

  If the section is found, the function checks where it is relative to the
  visible screen, then combines that with how far the page has already scrolled.
  This tells the browser where the section sits on the full page. The function
  leaves the requested header space, makes sure the destination is not above
  the start of the page, and asks the browser to scroll there smoothly.

  While the browser is moving toward "Skills", you can choose another menu item.
  That calls this function again with the new section id. The browser then heads
  toward the new destination instead of continuing the old smooth scroll.
*/